import type { FieldErrors } from "react-hook-form";

/**
 * Extracts error messages from a nested error object based on a dot-notation path.
 *
 * This function handles error messages in various nested structures including:
 * - Simple fields: errors[fieldName].message
 * - Array fields at index 0: errors[fieldName][0].message
 * - Repeater fields with dot notation: errors.basename[index].fieldName.message
 * - Multi-level nested fields: errors.basename.secondlevel[index].fieldName.message
 *
 * @param {string | undefined} slug - The dot-notation path to the error (e.g., "basename.0.fieldname")
 * @param {FieldErrors} errors - The errors object containing validation errors
 *
 * @returns {string | undefined} The error message if found, undefined otherwise
 *
 * @example
 * // Simple field error
 * repeaterErrorHandler("name", errors); // Returns errors.name.message
 *
 * @example
 * // Array field at index 0
 * repeaterErrorHandler("items", errors); // Returns errors.items[0].message if it exists
 *
 * @example
 * // Repeater field
 * repeaterErrorHandler("items.0.name", errors); // Returns errors.items[0].name.message
 *
 * @example
 * // Nested repeater field
 * repeaterErrorHandler("address.contact.0.email", errors); // Returns errors.address.contact[0].email.message
 */
const repeaterErrorHandler = (
  slug: string | undefined,
  errors: FieldErrors
): string | undefined => {
  // If the name includes a period, it's a nested field.
  const inRepeater = slug?.includes(".");

  let errorMessage;
  if (inRepeater) {
    const slugArray = slug!.split(".");

    // Start with the errors object

    let current = errors as any;

    // Traverse the path
    for (const part of slugArray) {
      // If the part is numeric, use it as an array index
      if (/^\d+$/.test(part)) {
        current = current?.[parseInt(part, 10)];
      } else {
        current = current?.[part];
      }

      // If current becomes undefined, exit early
      if (current === undefined) break;
    }

    // Get the error message
    errorMessage = current?.message;
  } else {
    if (slug) {
      // If in a group array
      if (Array.isArray(errors?.[slug]) && errors?.[slug]?.[0]) {
        errorMessage = errors?.[slug]![0]?.message;
      } else {
        errorMessage = errors?.[slug!]?.message;
      }
    }
  }

  return errorMessage;
};

export default repeaterErrorHandler;
